/**
 * SectionRenderer - Unified component renderer for layout sections.
 *
 * Single source of truth for rendering all component types from JSON.
 * Used by both the page builder editor (via renderToStaticMarkup) and public pages.
 *
 * @param {Object} props
 * @param {Object} props.content - Section content with components array
 * @param {string} props.sectionId - Section identifier
 * @param {string} props.mode - 'editor' | 'public'
 */

import React from 'react';
import { icons as LucideIcons } from 'lucide-react';
import { getFontFamilyCSS } from '../../utils/googleFonts';
import { colorValueToCss, colorValueToSolidCss } from '../fields/ColorPicker/colorUtils';
import {
  generateResponsiveCSS,
  formatDimension,
  getResponsiveDimension,
  getResponsiveDirection,
  getResponsiveSpacing,
  formatSpacingValue,
  formatSpacingCSS,
  resolveSpacing,
  resolveAllStyles,
} from './StyleResolver';
import { getFlexChildStyles, resolveFlexChildForBreakpoint } from '../fields/FlexChildField';
import { resolveLinkUrl, hasLinkUrl, COLUMN_LAYOUT_PRESETS, sanitizeCSS } from './renderUtils';
import { sanitizeHTML } from '../../utils/htmlSanitizer';
import AccordionPublic from './AccordionPublic';
import VideoWithPlayButton from './VideoWithPlayButton';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate a stable component ID, falling back to a random one
 */
function getComponentId(component, fallbackPrefix) {
  return component.id || `${fallbackPrefix}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Build responsive + custom CSS string for a component
 */
function buildComponentCSS(component, componentId) {
  const responsiveCSS = generateResponsiveCSS(component, componentId, null);
  let customCSSBlock = '';
  if (component.props?.customCSS && component.props.customCSS.trim()) {
    customCSSBlock = `\n#${componentId} { ${sanitizeCSS(component.props.customCSS)} }`;
  }
  return (responsiveCSS || '') + customCSSBlock;
}

/**
 * Convert string/boolean to boolean
 */
function toBool(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return !!value;
}

/**
 * Extract YouTube video ID from a watch, short, or embed URL.
 * Supports: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID,
 *           youtube.com/shorts/ID, youtube.com/live/ID
 */
function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

/**
 * Extract Vimeo video ID from a standard or player URL.
 * Supports: vimeo.com/ID, player.vimeo.com/video/ID, vimeo.com/channels/.../ID
 */
function extractVimeoId(url) {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/)?(\d+)/);
  return match ? match[1] : null;
}

/**
 * Format a video dimension object to CSS value
 */
function formatVideoDimension(dimensionObj) {
  if (!dimensionObj) return 'auto';
  if (dimensionObj.value === 'none' || dimensionObj.value === 'auto') return dimensionObj.value;
  return `${dimensionObj.value}${dimensionObj.unit}`;
}

/**
 * Calculate video dimensions based on aspect ratio and sizing props
 */
function getVideoDimensions(props) {
  const containerWidth = props.containerWidth
    ? formatVideoDimension(props.containerWidth)
    : '100%';
  const maxWidth = props.maxWidth
    ? formatVideoDimension(props.maxWidth)
    : 'none';

  if (props.aspectRatio === 'none') {
    return { containerWidth, maxWidth, width: '100%', height: 'auto', isNaturalRatio: true, isAspectRatioMode: false };
  }

  if (props.aspectRatio === 'custom') {
    return {
      containerWidth, maxWidth,
      width: formatDimension(props.customWidth) || formatDimension(props.width) || '640px',
      height: formatDimension(props.customHeight) || formatDimension(props.height) || '360px',
      isCustom: true, isAspectRatioMode: false,
    };
  }

  const aspectRatios = { '16:9': '56.25%', '4:3': '75%', '1:1': '100%', '9:16': '177.78%', '21:9': '42.86%' };
  return { containerWidth, maxWidth, width: '100%', paddingBottom: aspectRatios[props.aspectRatio] || aspectRatios['16:9'], isAspectRatioMode: true };
}

/**
 * Calculate column flex style based on row layout and span
 */
function getColumnFlexStyle(column, rowProps, columnIndex) {
  const rawSpan = column.props.span;
  let baseSpan;

  if (typeof rawSpan === 'object' && rawSpan !== null && rawSpan.base !== undefined) {
    baseSpan = rawSpan.base;
  } else if (typeof rawSpan === 'number') {
    baseSpan = rawSpan;
  } else {
    baseSpan = undefined;
  }

  const rowColumnLayout = rowProps?.columnLayout;
  const layoutValue = typeof rowColumnLayout === 'object' && rowColumnLayout?.base !== undefined
    ? rowColumnLayout.base
    : rowColumnLayout;

  if (rowProps && layoutValue && layoutValue !== 'equal' && layoutValue !== 'custom') {
    let widths = rowProps.columnWidths;
    if (COLUMN_LAYOUT_PRESETS[layoutValue]) {
      widths = COLUMN_LAYOUT_PRESETS[layoutValue];
    }
    if (widths && widths.length > columnIndex) {
      const presetSpan = widths[columnIndex];
      if (presetSpan === 12) return { flex: '0 0 100%', maxWidth: '100%' };
      return { flex: `${presetSpan} 1 0%` };
    }
  }

  if (baseSpan !== undefined && baseSpan !== 6) {
    if (baseSpan === 12) return { flex: '0 0 100%', maxWidth: '100%' };
    return { flex: `${baseSpan} 1 0%` };
  }

  return { flex: '1 1 0%' };
}

/**
 * Build section-level responsive CSS (padding, margin, sizing, visibility)
 */
function buildSectionResponsiveCSS(content, sectionId) {
  const cssRules = [];

  if (content.padding?.base !== undefined) {
    if (content.padding.tablet) cssRules.push(`@media (min-width: 768px) { #${sectionId} { ${formatSpacingCSS(content.padding.tablet, 'padding')} } }`);
    if (content.padding.desktop) cssRules.push(`@media (min-width: 1024px) { #${sectionId} { ${formatSpacingCSS(content.padding.desktop, 'padding')} } }`);
  }

  if (content.margin?.base !== undefined) {
    if (content.margin.tablet) cssRules.push(`@media (min-width: 768px) { #${sectionId} { ${formatSpacingCSS(content.margin.tablet, 'margin')} } }`);
    if (content.margin.desktop) cssRules.push(`@media (min-width: 1024px) { #${sectionId} { ${formatSpacingCSS(content.margin.desktop, 'margin')} } }`);
  }

  const sizingKeys = [
    { prop: 'width', css: 'width' },
    { prop: 'height', css: 'height' },
    { prop: 'minWidth', css: 'min-width' },
    { prop: 'minHeight', css: 'min-height' },
    { prop: 'maxWidth', css: 'max-width' },
    { prop: 'maxHeight', css: 'max-height' },
  ];
  sizingKeys.forEach(({ prop, css }) => {
    const val = content[prop];
    if (val?.tablet || val?.desktop) {
      if (val.tablet) {
        const v = formatDimension(val.tablet);
        if (v !== 'auto') cssRules.push(`@media (min-width: 768px) { #${sectionId} { ${css}: ${v} !important; } }`);
      }
      if (val.desktop) {
        const v = formatDimension(val.desktop);
        if (v !== 'auto') cssRules.push(`@media (min-width: 1024px) { #${sectionId} { ${css}: ${v} !important; } }`);
      }
    }
  });

  if (content.visibility && typeof content.visibility === 'object') {
    if (content.visibility.base === false) cssRules.push(`#${sectionId} { display: none !important; }`);
    if (content.visibility.tablet === false) cssRules.push(`@media (min-width: 768px) { #${sectionId} { display: none !important; } }`);
    else if (content.visibility.tablet === true) cssRules.push(`@media (min-width: 768px) { #${sectionId} { display: flex !important; } }`);
    if (content.visibility.desktop === false) cssRules.push(`@media (min-width: 1024px) { #${sectionId} { display: none !important; } }`);
    else if (content.visibility.desktop === true) cssRules.push(`@media (min-width: 1024px) { #${sectionId} { display: flex !important; } }`);
  }

  return cssRules.join('\n');
}

// ============================================================================
// Component Renderers
// ============================================================================

/**
 * Render a single component (the main switch/case dispatcher)
 */
function renderComponent(component, mode, path, cssCollector) {
  if (!component) return null;

  const { type, props = {} } = component;
  const componentId = getComponentId(component, type);
  const allCSS = buildComponentCSS(component, componentId);

  // Collect CSS for public mode (single <style> block), inject inline for editor mode
  if (allCSS && cssCollector) cssCollector.push(allCSS);

  // Flex child base styles
  const flexChildBaseStyles = getFlexChildStyles(props.flexChild, 'base');
  const hasFlexChild = Object.keys(flexChildBaseStyles).length > 0;

  // Editor data attributes
  const editorAttrs = mode === 'editor' ? {
    'data-element-id': path,
    'data-component-type': type,
  } : {};

  // Helper: wrap element with CSS injection, ID, and flex child styles
  const withStyles = (element, id = componentId, extraEditorAttrs = {}) => {
    const cloneProps = { id, ...editorAttrs, ...extraEditorAttrs };
    if (hasFlexChild) {
      cloneProps.style = { ...(element.props.style || {}), ...flexChildBaseStyles };
    }

    return (
      <>
        {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
        {React.cloneElement(element, cloneProps)}
      </>
    );
  };

  switch (type) {
    case 'text': {
      const TextElement = props.element || 'p';
      let textContent = 'Text content';
      if (typeof props.content === 'string') {
        textContent = props.content;
      } else if (Array.isArray(props.content)) {
        textContent = props.content.map(item => item.content || item.text || '').join('');
      }

      const editableAttr = mode === 'editor' ? { 'data-editable': 'true' } : {};

      return withStyles(
        <TextElement style={resolveAllStyles(component, 'base')}>
          {/<[^>]+>/.test(textContent)
            ? <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(textContent) }} />
            : textContent}
        </TextElement>,
        componentId,
        editableAttr
      );
    }

    case 'image': {
      if (!props.src) return null;
      return withStyles(
        <img src={props.src} alt={props.alt || ''} style={resolveAllStyles(component, 'base')} />
      );
    }

    case 'button': {
      const buttonStyle = resolveAllStyles(component, 'base');
      const editableAttr = mode === 'editor' ? { 'data-editable': 'true' } : {};

      // Public mode: hover effects
      const hoverHandlers = mode === 'public' ? {
        onMouseEnter: (e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; },
        onMouseLeave: (e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; },
      } : {};

      const ButtonEl = (
        <button
          id={componentId}
          style={buttonStyle}
          {...editorAttrs}
          {...editableAttr}
          {...hoverHandlers}
        >
          {props.text || 'Button'}
        </button>
      );

      if (hasLinkUrl(props.url)) {
        const resolvedUrl = resolveLinkUrl(props.url);
        return (
          <>
            {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
            <a
              href={resolvedUrl}
              style={{ display: 'inline-block', textDecoration: 'none', ...flexChildBaseStyles }}
              target={props.openInNewTab ? '_blank' : '_self'}
              rel={props.openInNewTab ? 'noopener noreferrer' : undefined}
            >
              {ButtonEl}
            </a>
          </>
        );
      }

      return (
        <>
          {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
          {hasFlexChild
            ? React.cloneElement(ButtonEl, { style: { ...buttonStyle, ...flexChildBaseStyles } })
            : ButtonEl}
        </>
      );
    }

    case 'link': {
      const linkStyle = {
        ...resolveAllStyles(component, 'base'),
        cursor: 'pointer',
        display: 'inline',
      };
      const editableAttr = mode === 'editor' ? { 'data-editable': 'true' } : {};

      if (hasLinkUrl(props.url)) {
        const resolvedUrl = resolveLinkUrl(props.url);
        return withStyles(
          <a
            href={resolvedUrl}
            target={props.openInNewTab ? '_blank' : '_self'}
            rel={props.openInNewTab ? 'noopener noreferrer' : undefined}
            style={linkStyle}
          >
            {props.text || 'Link'}
          </a>,
          componentId,
          editableAttr
        );
      }

      return withStyles(
        <span style={linkStyle}>{props.text || 'Link'}</span>,
        componentId,
        editableAttr
      );
    }

    case 'icon': {
      const iconName = props.icon || props.name || 'Star';
      const normalizedIconName = iconName ? iconName.charAt(0).toUpperCase() + iconName.slice(1) : 'Star';
      const IconComponent = LucideIcons[normalizedIconName] || LucideIcons[iconName];
      const iconColor = colorValueToSolidCss(props.color, '#374151');
      const iconType = props.iconType || 'lucide';
      const iconSize = props.size || 24;
      const iconContainerStyle = resolveAllStyles(component, 'base');

      if (iconType === 'emoji') {
        return withStyles(
          <span style={{ ...iconContainerStyle, fontSize: `${iconSize}px`, lineHeight: 1 }}>
            {props.icon || '\u2B50'}
          </span>
        );
      }

      if (iconType === 'custom' && props.customIcon) {
        const customWidth = formatDimension(props.width) || `${iconSize}px`;
        const customHeight = formatDimension(props.height) || `${iconSize}px`;

        if (props.customIcon.startsWith('http')) {
          return withStyles(
            <span style={iconContainerStyle}>
              <img src={props.customIcon} alt="Icon" style={{ width: customWidth, height: customHeight, objectFit: 'contain' }} />
            </span>
          );
        }
        return withStyles(
          <span style={iconContainerStyle}>
            <span
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: customWidth, height: customHeight }}
              dangerouslySetInnerHTML={{ __html: props.customIcon }}
            />
          </span>
        );
      }

      if (!IconComponent) {
        return withStyles(
          <span style={{ ...iconContainerStyle, fontSize: `${iconSize}px`, color: iconColor }}>
            {'\u2605'}
          </span>
        );
      }

      return withStyles(
        <span style={iconContainerStyle}>
          <IconComponent
            size={iconSize}
            color={iconColor}
            strokeWidth={props.style === 'solid' ? 0 : 2}
            fill={props.style === 'solid' ? iconColor : 'none'}
          />
        </span>
      );
    }

    case 'spacer':
      return withStyles(<div style={resolveAllStyles(component, 'base')} />);

    case 'listItem': {
      const isVertical = props.direction === 'vertical';
      let listItemText = 'List item text';
      if (typeof props.text === 'string') {
        listItemText = props.text;
      } else if (Array.isArray(props.text)) {
        listItemText = props.text.map(item => item.text || '').join('');
      }

      return withStyles(
        <div style={resolveAllStyles(component, 'base')}>
          <div style={{
            flexShrink: 0,
            width: isVertical ? 'auto' : `${props.iconSize || 48}px`,
            height: `${props.iconSize || 48}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {props.iconUrl ? (
              <img src={props.iconUrl} alt="Icon" style={{ width: `${props.iconSize || 48}px`, height: `${props.iconSize || 48}px`, objectFit: 'contain' }} />
            ) : (
              <div style={{ width: `${props.iconSize || 48}px`, height: `${props.iconSize || 48}px`, backgroundColor: '#e5e7eb', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                {'\uD83D\uDDBC\uFE0F'}
              </div>
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: isVertical ? 'center' : 'flex-start', textAlign: isVertical ? 'center' : 'left' }}>
            <p style={{
              fontFamily: props.fontFamily ? getFontFamilyCSS(props.fontFamily) : undefined,
              fontSize: typeof props.fontSize === 'number'
                ? `${props.fontSize}px`
                : props.fontSize?.base
                  ? `${props.fontSize.base.value}${props.fontSize.base.unit}`
                  : '16px',
              fontWeight: props.fontWeight || 'normal',
              color: colorValueToSolidCss(props.textColor, '#1f2937'),
              lineHeight: props.lineHeight || 1.5,
              letterSpacing: props.letterSpacing ? `${props.letterSpacing}px` : undefined,
              margin: 0,
            }}>
              {listItemText}
            </p>
          </div>
        </div>
      );
    }

    case 'video': {
      if (!props.url) return null;

      const url = props.url || '';
      const videoType = props.videoType || '';

      // Determine if this is a YouTube or Vimeo embed
      const isYouTube = videoType === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be');
      const isVimeo = videoType === 'vimeo' || (url.includes('vimeo.com') && !isYouTube);

      // Build aspect-ratio container dimensions
      const aspectRatios = { '16:9': '56.25%', '4:3': '75%', '1:1': '100%', '21:9': '42.86%', '9:16': '177.78%' };
      const paddingBottom = aspectRatios[props.aspectRatio] || '56.25%';

      const containerStyle = {
        position: 'relative',
        paddingBottom,
        height: 0,
        overflow: 'hidden',
        borderRadius: `${props.borderRadius || 0}px`,
        border: props.borderWidth > 0
          ? `${props.borderWidth}px ${props.borderStyle || 'solid'} ${colorValueToSolidCss(props.borderColor, 'transparent')}`
          : 'none',
      };

      const embedStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: 'none',
      };

      if (isYouTube) {
        const videoId = extractYouTubeId(url);
        if (!videoId) {
          return withStyles(
            <div style={resolveAllStyles(component, 'base')}>
              <div style={{ ...containerStyle, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#9ca3af', fontSize: '14px' }}>
                  Invalid YouTube URL
                </span>
              </div>
            </div>
          );
        }
        const params = [];
        if (props.autoplay) params.push('autoplay=1');
        if (props.muted) params.push('mute=1');
        if (props.loop) params.push(`loop=1&playlist=${videoId}`);
        if (!toBool(props.controls !== undefined ? props.controls : true)) params.push('controls=0');
        const embedUrl = `https://www.youtube.com/embed/${videoId}${params.length ? '?' + params.join('&') : ''}`;

        return withStyles(
          <div style={resolveAllStyles(component, 'base')}>
            <div style={containerStyle}>
              <iframe
                src={embedUrl}
                style={embedStyle}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        );
      }

      if (isVimeo) {
        const videoId = extractVimeoId(url);
        if (!videoId) {
          return withStyles(
            <div style={resolveAllStyles(component, 'base')}>
              <div style={{ ...containerStyle, background: '#f3f4f6' }}>
                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#9ca3af', fontSize: '14px' }}>
                  Invalid Vimeo URL
                </span>
              </div>
            </div>
          );
        }
        const params = [];
        if (props.autoplay) params.push('autoplay=1');
        if (props.muted) params.push('muted=1');
        if (props.loop) params.push('loop=1');
        const embedUrl = `https://player.vimeo.com/video/${videoId}${params.length ? '?' + params.join('&') : ''}`;

        return withStyles(
          <div style={resolveAllStyles(component, 'base')}>
            <div style={containerStyle}>
              <iframe
                src={embedUrl}
                style={embedStyle}
                title="Vimeo video"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        );
      }

      // Direct / MP4 video
      const dimensions = getVideoDimensions(props);

      const videoContent = mode === 'public'
        ? <VideoWithPlayButton videoProps={props} dimensions={dimensions} />
        : (
          <video
            src={url}
            poster={props.posterUrl || undefined}
            controls={toBool(props.controls !== undefined ? props.controls : true)}
            autoPlay={toBool(props.autoplay)}
            loop={toBool(props.loop)}
            muted={toBool(props.muted)}
            playsInline
            preload={props.preload || 'metadata'}
            style={{
              width: dimensions.isAspectRatioMode || dimensions.isNaturalRatio ? '100%' : dimensions.width,
              height: dimensions.isAspectRatioMode ? '100%' : dimensions.height,
              maxWidth: '100%',
              position: dimensions.isAspectRatioMode ? 'absolute' : 'static',
              top: dimensions.isAspectRatioMode ? 0 : undefined,
              left: dimensions.isAspectRatioMode ? 0 : undefined,
              objectFit: props.objectFit || 'contain',
              borderRadius: `${props.borderRadius || 8}px`,
              opacity: (props.opacity || 100) / 100,
              background: colorValueToCss(props.backgroundColor, '#000000'),
              display: 'block',
            }}
          >
            <source src={url} type={props.type || 'video/mp4'} />
          </video>
        );

      return withStyles(
        <div style={resolveAllStyles(component, 'base')}>
          <div style={{ width: dimensions.containerWidth, maxWidth: dimensions.maxWidth, margin: '0 auto' }}>
            <div style={{
              position: dimensions.isAspectRatioMode ? 'relative' : 'static',
              paddingBottom: dimensions.isAspectRatioMode ? dimensions.paddingBottom : undefined,
              height: dimensions.isAspectRatioMode ? 0 : 'auto',
              background: colorValueToCss(props.backgroundColor, '#000000'),
              borderRadius: `${props.borderRadius || 8}px`,
              border: props.borderWidth > 0 ? `${props.borderWidth}px ${props.borderStyle || 'solid'} ${colorValueToSolidCss(props.borderColor, 'transparent')}` : 'none',
            }}>
              {videoContent}
            </div>
          </div>
        </div>
      );
    }

    case 'accordion': {
      if (mode === 'public') {
        return withStyles(<AccordionPublic component={component} />);
      }

      // Editor mode: native <details> elements
      const items = props.items || [];
      const headerPadding = getResponsiveSpacing(props.headerPadding, 'base') || { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' };
      const contentPadding = getResponsiveSpacing(props.contentPadding, 'base') || { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' };

      const containerStyle = {
        ...resolveAllStyles(component, 'base'),
        backgroundColor: colorValueToSolidCss(props.backgroundColor, '#ffffff'),
        overflow: 'hidden',
      };

      return withStyles(
        <div style={containerStyle}>
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            return (
              <details
                key={item.id || idx}
                style={{
                  borderBottom: !isLast ? `${props.borderWidth || 1}px ${props.borderStyle || 'solid'} ${colorValueToSolidCss(props.borderColor, '#e5e7eb')}` : 'none',
                  marginBottom: props.itemGap ? `${props.itemGap}px` : '0',
                }}
                open={item.defaultOpen || undefined}
              >
                <summary style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: props.iconPosition === 'left' ? 'row-reverse' : 'row',
                  backgroundColor: colorValueToSolidCss(props.headerBackgroundColor, '#f9fafb'),
                  color: colorValueToSolidCss(props.headerTextColor, '#1f2937'),
                  fontFamily: props.headerFontFamily && props.headerFontFamily !== 'System Default' ? props.headerFontFamily : 'inherit',
                  fontSize: `${props.headerFontSize || 16}px`,
                  fontWeight: props.headerFontWeight || '600',
                  padding: `${formatSpacingValue(headerPadding.top, headerPadding.unit)} ${formatSpacingValue(headerPadding.right, headerPadding.unit)} ${formatSpacingValue(headerPadding.bottom, headerPadding.unit)} ${formatSpacingValue(headerPadding.left, headerPadding.unit)}`,
                  cursor: 'pointer',
                  listStyle: 'none',
                }}>
                  <span>{item.title}</span>
                  {props.iconType !== 'none' && (
                    <svg className="accordion-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </summary>
                <div style={{
                  backgroundColor: colorValueToSolidCss(props.contentBackgroundColor, '#ffffff'),
                  color: colorValueToSolidCss(props.contentTextColor, '#4b5563'),
                  fontFamily: props.contentFontFamily && props.contentFontFamily !== 'System Default' ? props.contentFontFamily : 'inherit',
                  fontSize: `${props.contentFontSize || 14}px`,
                  padding: `${formatSpacingValue(contentPadding.top, contentPadding.unit)} ${formatSpacingValue(contentPadding.right, contentPadding.unit)} ${formatSpacingValue(contentPadding.bottom, contentPadding.unit)} ${formatSpacingValue(contentPadding.left, contentPadding.unit)}`,
                }}>
                  {item.content}
                </div>
              </details>
            );
          })}
        </div>
      );
    }

    case 'row':
      return renderRow(component, mode, path, cssCollector);

    case 'column':
      return renderColumn(component, {}, 0, mode, path, cssCollector);

    case 'container':
      return renderContainer(component, mode, path, cssCollector);

    case 'block':
      return renderBlock(component, mode, path, cssCollector);

    default:
      if (mode === 'editor') {
        return null; // Editor: silent for unknown types
      }
      console.warn(`Unknown component type: ${type}`);
      return null;
  }
}

// ============================================================================
// Layout Renderers
// ============================================================================

function renderColumn(column, rowProps, columnIndex, mode, path, cssCollector) {
  if (!column) return null;

  const stableColumnId = getComponentId(column, `column-${columnIndex}`);
  const allCSS = buildComponentCSS(column, stableColumnId);
  if (allCSS && cssCollector) cssCollector.push(allCSS);
  const baseStyle = resolveAllStyles(column, 'base');
  const flexStyle = getColumnFlexStyle(column, rowProps, columnIndex);

  const resolvedColumnFlexChild = resolveFlexChildForBreakpoint(column.props.flexChild, 'base');
  const columnFlexChildStyles = resolvedColumnFlexChild?.mode && resolvedColumnFlexChild.mode !== 'auto'
    ? getFlexChildStyles(column.props.flexChild, 'base')
    : {};

  const editorAttrs = mode === 'editor' ? {
    'data-element-id': path,
    'data-component-type': 'column',
    'data-can-drop': 'true',
    'data-flex-direction': 'column',
  } : {};

  return (
    <>
      {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
      <div
        id={stableColumnId}
        style={{ ...baseStyle, ...flexStyle, ...columnFlexChildStyles }}
        {...editorAttrs}
      >
        {column.children?.map((child, idx) => (
          <React.Fragment key={child.id || idx}>
            {renderComponent(child, mode, path ? `${path}.children.${idx}` : `${idx}`, cssCollector)}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

function renderRow(row, mode, path, cssCollector) {
  if (!row) return null;

  const rowId = getComponentId(row, 'row');
  const allCSS = buildComponentCSS(row, rowId);
  if (allCSS && cssCollector) cssCollector.push(allCSS);

  const direction = getResponsiveDirection(row.props.direction, 'base');
  const flexDirection = direction === 'vertical' ? 'column' : 'row';

  const editorAttrs = mode === 'editor' ? {
    'data-element-id': path,
    'data-component-type': 'row',
    'data-can-drop': 'true',
    'data-flex-direction': flexDirection,
  } : {};

  return (
    <>
      {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
      <div
        id={rowId}
        style={{ ...resolveAllStyles(row, 'base'), width: '100%' }}
        {...editorAttrs}
      >
        {row.children?.map((child, idx) => {
          const childPath = path ? `${path}.children.${idx}` : `${idx}`;
          return (
            <React.Fragment key={child.id || idx}>
              {child.type === 'column'
                ? renderColumn(child, row.props, idx, mode, childPath, cssCollector)
                : renderComponent(child, mode, childPath, cssCollector)}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

function renderContainer(container, mode, path, cssCollector) {
  if (!container) return null;

  const containerId = getComponentId(container, 'container');
  const allCSS = buildComponentCSS(container, containerId);
  if (allCSS && cssCollector) cssCollector.push(allCSS);
  const containerProps = container.props || {};

  const editorAttrs = mode === 'editor' ? {
    'data-element-id': path,
    'data-component-type': 'container',
    'data-can-drop': 'true',
    'data-flex-direction': containerProps.flexDirection || 'column',
  } : {};

  return (
    <>
      {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
      <div
        id={containerId}
        style={resolveAllStyles(container, 'base')}
        {...editorAttrs}
      >
        {container.children?.map((child, idx) => (
          <React.Fragment key={child.id || idx}>
            {renderComponent(child, mode, path ? `${path}.children.${idx}` : `${idx}`, cssCollector)}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

function renderBlock(block, mode, path, cssCollector) {
  if (!block) return null;

  const blockId = getComponentId(block, 'block');
  const allCSS = buildComponentCSS(block, blockId);
  if (allCSS && cssCollector) cssCollector.push(allCSS);
  const blockProps = block.props || {};

  const displayMode = blockProps.display || 'block';
  const isFlex = displayMode === 'flex' || displayMode === 'inline-flex';
  const direction = isFlex ? getResponsiveDirection(blockProps.direction, 'base') : 'column';
  const flexDirection = direction === 'vertical' ? 'column' : 'row';

  const editorAttrs = mode === 'editor' ? {
    'data-element-id': path,
    'data-component-type': 'block',
    'data-can-drop': 'true',
    'data-flex-direction': flexDirection,
  } : {};

  return (
    <>
      {allCSS && !cssCollector && <style dangerouslySetInnerHTML={{ __html: allCSS }} />}
      <div
        id={blockId}
        style={resolveAllStyles(block, 'base')}
        {...editorAttrs}
      >
        {block.children?.map((child, idx) => (
          <React.Fragment key={child.id || idx}>
            {renderComponent(child, mode, path ? `${path}.children.${idx}` : `${idx}`, cssCollector)}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

// ============================================================================
// Section Container
// ============================================================================

/**
 * Build section container styles from content props
 */
function buildSectionContainerStyle(content) {
  const hasBackgroundImage = content.backgroundImage || content.backgroundImageUrl;
  const backgroundImageSrc = content.backgroundImage || content.backgroundImageUrl;

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    textAlign: content.textAlign || 'left',
    background: hasBackgroundImage ? undefined : colorValueToCss(content.sectionBackgroundColor, 'transparent'),
    backgroundImage: hasBackgroundImage ? `url(${backgroundImageSrc})` : undefined,
    backgroundSize: hasBackgroundImage ? (content.backgroundSize || 'cover') : undefined,
    backgroundPosition: hasBackgroundImage ? (content.backgroundPosition || 'center') : undefined,
    backgroundRepeat: hasBackgroundImage ? 'no-repeat' : undefined,
    overflow: content.overflow && content.overflow !== 'visible' ? content.overflow : undefined,
  };

  // Spacing
  if (content.padding && content.padding.base !== undefined) {
    Object.assign(containerStyle, resolveSpacing(content.padding, 'padding', 'base'));
  } else {
    containerStyle.paddingTop = content.paddingTop ? `${content.paddingTop}px` : '0';
    containerStyle.paddingBottom = content.paddingBottom ? `${content.paddingBottom}px` : '0';
    containerStyle.paddingLeft = content.paddingLeft ? `${content.paddingLeft}px` : '0';
    containerStyle.paddingRight = content.paddingRight ? `${content.paddingRight}px` : '0';
  }
  if (content.margin && content.margin.base !== undefined) {
    Object.assign(containerStyle, resolveSpacing(content.margin, 'margin', 'base'));
  } else {
    containerStyle.marginTop = content.marginTop ? `${content.marginTop}px` : '0';
    containerStyle.marginBottom = content.marginBottom ? `${content.marginBottom}px` : '0';
  }

  // Sizing
  ['width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight'].forEach(prop => {
    if (content[prop]) {
      const v = getResponsiveDimension(content[prop], 'base');
      if (v !== 'auto') containerStyle[prop] = v;
    }
  });

  // Border
  if (content.sectionBorderWidth && content.sectionBorderWidth > 0) {
    containerStyle.borderWidth = `${content.sectionBorderWidth}px`;
    containerStyle.borderStyle = content.sectionBorderStyle || 'solid';
    containerStyle.borderColor = colorValueToSolidCss(content.sectionBorderColor, 'transparent');
  }
  if (content.sectionBorderRadius && content.sectionBorderRadius > 0) {
    containerStyle.borderRadius = `${content.sectionBorderRadius}px`;
  }

  return containerStyle;
}

// ============================================================================
// Main Export
// ============================================================================

export default function SectionRenderer({ content, sectionId, mode = 'public' }) {
  if (!content?.components || content.components.length === 0) {
    if (mode === 'editor') {
      return (
        <div
          data-section-id={sectionId}
          data-can-drop="true"
          data-flex-direction="column"
          style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', minHeight: '64px' }}
        >
          <p style={{ pointerEvents: 'none' }}>Empty section — drop a component here</p>
        </div>
      );
    }
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af' }}>
        <p>No layout components</p>
      </div>
    );
  }

  const uniqueId = sectionId || `section-${Math.random().toString(36).substr(2, 9)}`;
  const containerStyle = buildSectionContainerStyle(content);
  const responsiveCSS = buildSectionResponsiveCSS(content, uniqueId);

  // Public mode: collect all CSS into a single <style> block
  // Editor mode: each component injects CSS inline (no collector)
  const cssCollector = mode === 'public' ? [] : null;
  if (responsiveCSS && cssCollector) cssCollector.push(responsiveCSS);

  const editorAttrs = mode === 'editor' ? {
    'data-section-id': sectionId,
    'data-can-drop': 'true',
    'data-flex-direction': 'column',
  } : {};

  // Compute children first so cssCollector is populated before we render the <style> tag
  const children = content.components.map((component, idx) => (
    <React.Fragment key={component.id || idx}>
      {renderComponent(component, mode, `${idx}`, cssCollector)}
    </React.Fragment>
  ));

  return (
    <>
      {cssCollector && cssCollector.length > 0 && (
        <style dangerouslySetInnerHTML={{ __html: cssCollector.join('\n') }} />
      )}
      {!cssCollector && responsiveCSS && <style dangerouslySetInnerHTML={{ __html: responsiveCSS }} />}
      <div id={uniqueId} style={containerStyle} {...editorAttrs}>
        {children}
      </div>
    </>
  );
}
